import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaCaretDown } from 'react-icons/fa';
import {
  Link,
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import HoverLogo from '../images/hover-logo.svg';
import HoverLogoWhite from '../images/hover-logo-white.svg';
import { URLS } from '../v2/constants';

const SolutionsDropdown = ({ isWhite }: { isWhite: boolean }) => (
  <Menu placement="bottom">
    <MenuButton
      as={Button}
      rightIcon={<Icon as={FaCaretDown} />}
      color={{ base: '#247ddc', lg: isWhite ? 'white' : '#247ddc' }}
      bgColor="transparent"
      border="none"
      fontWeight="800"
      px="0"
      _hover={{
        bgColor: 'transparent',
        border: 'none',
        outline: 'none',
        textDecoration: 'underline',
      }}
      _focus={{
        boxShadow: 'none',
      }}
    >
      Solutions
    </MenuButton>
    <MenuList>
      <MenuItem as={Link} href="/remote-engagement-for-contractors/">
        Remote Engagement
      </MenuItem>
      <MenuItem as={Link} href="/exterior-contractors/">
        Home Improvement Professionals
      </MenuItem>
      <MenuItem as={Link} href="/restoration/">
        Restoration Professionals
      </MenuItem>
      <MenuItem as={Link} href="/insurance/">
        Insurance Carriers
      </MenuItem>
      <MenuItem as={Link} href="/measurement-app-architects/">
        Architects & Designers
      </MenuItem>
    </MenuList>
  </Menu>
);

interface MenuToggleProps {
  toggle: () => void;
  isOpen: boolean;
  isWhite: boolean;
}

const MenuToggle = ({ toggle, isOpen, isWhite }: MenuToggleProps) => (
  <Box display={{ base: 'block', lg: 'none' }} onClick={toggle} zIndex={10000}>
    {isOpen ? (
      <FaTimes size="24" />
    ) : (
      <FaBars size="24" color={isWhite ? 'white' : ''} />
    )}
  </Box>
);

interface NavLinkProps {
  isWhite: boolean;
  children: React.ReactNode;
  to: string;
}

const NavLink = ({ isWhite, children, to }: NavLinkProps) => (
  <Link
    href={to}
    color={{ base: '#247ddc', lg: isWhite ? 'white' : '#247ddc' }}
    fontWeight="800"
  >
    <Text display="block">{children}</Text>
  </Link>
);

const MenuLinks = ({
  isOpen,
  isWhite,
}: {
  isOpen: boolean;
  isWhite: boolean;
}) => (
  <Box
    display={{ base: isOpen ? 'block' : 'none', lg: 'block' }}
    flexBasis={{ base: '100%', lg: 'auto' }}
  >
    <Stack
      spacing={5}
      align="center"
      justify={{ base: 'center', lg: 'flex-end' }}
      direction={{ base: 'column', lg: 'row' }}
      pt={{ base: '4', lg: 0 }}
      position={{ base: 'fixed', lg: 'unset' }}
      width={{ base: '100%', lg: 'unset' }}
      height={{ base: '100%', lg: 'unset' }}
      bg={{ base: 'white', lg: 'unset' }}
      top={0}
      left={0}
      right={0}
      bottom={0}
    >
      <SolutionsDropdown isWhite={isWhite} />
      <NavLink to="/product" isWhite={isWhite}>
        Product
      </NavLink>
      <NavLink to="/pricing" isWhite={isWhite}>
        Pricing
      </NavLink>
      <NavLink to="/jobs" isWhite={isWhite}>
        Jobs
      </NavLink>
      <NavLink to="/login" isWhite={isWhite}>
        Log In
      </NavLink>
      <NavLink to={URLS.navBar.scheduleDemo} isWhite={isWhite}>
        <Button
          size="lg"
          px="7"
          fontSize="sm"
          fontWeight="extrabold"
          variant="outline"
        >
          Schedule a Demo
        </Button>
      </NavLink>
      <NavLink to="/signup" isWhite={isWhite}>
        <Button
          size="lg"
          px="10"
          color="white"
          fontSize="sm"
          fontWeight="extrabold"
          bg={['#247ddc', '#247ddc', 'primary.500', 'primary.500']}
        >
          SIGN UP
        </Button>
      </NavLink>
    </Stack>
  </Box>
);

interface NavbarContainerProps {
  navbarScrolled: boolean;
  isNavInitiallyTransparent: boolean;
  children: React.ReactNode;
}

const NavbarContainer = ({
  navbarScrolled,
  isNavInitiallyTransparent,
  children,
}: NavbarContainerProps) => (
  <Flex
    as="nav"
    align="center"
    justify="space-between"
    wrap="wrap"
    position={navbarScrolled ? 'fixed' : 'absolute'}
    mx="auto"
    w="100%"
    mb={8}
    py={[4, 5]}
    px={[8, 20]}
    bg={navbarScrolled || !isNavInitiallyTransparent ? 'white' : 'transparent'}
    color={['black', 'black', 'primary.700', 'primary.700']}
    zIndex="100"
  >
    {children}
  </Flex>
);

interface NavbarProps {
  isNavInitiallyWhite?: boolean;
  isNavInitiallyTransparent?: boolean;
}

const NavBar = ({
  isNavInitiallyWhite = false,
  isNavInitiallyTransparent = true,
}: NavbarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [navbarScrolled, setNavbarScrolled] = useState(false);

  useEffect(() => {
    const scrollHandler = () => setNavbarScrolled(window.scrollY >= 110);
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);

  const toggle = () => setIsOpen(!isOpen);
  const isWhite = isNavInitiallyWhite && !navbarScrolled;

  return (
    <NavbarContainer
      navbarScrolled={navbarScrolled}
      isNavInitiallyTransparent={isNavInitiallyTransparent}
    >
      <Link href="/" zIndex="1000">
        {isWhite && !isOpen ? (
          <HoverLogoWhite
            title="HOVER"
            alt="HOVER White Logo"
            style={{ width: 180 }}
          />
        ) : (
          <HoverLogo title="HOVER" alt="HOVER Logo" style={{ width: 180 }} />
        )}
      </Link>
      <MenuToggle toggle={toggle} isOpen={isOpen} isWhite={isWhite} />
      <MenuLinks isOpen={isOpen} isWhite={isWhite} />
    </NavbarContainer>
  );
};

NavBar.defaultProps = {
  isNavInitiallyTransparent: true,
  isNavInitiallyWhite: false,
};

export default NavBar;
